import { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import { ActionCardsPanel } from './ActionCardsPanel';

interface SpaceInsertPointProps {
  onInsert: (blockType: string) => void;
  onAIAsk?: (prompt: string) => void;
  autoOpen?: boolean;
}

export const SpaceInsertPoint = ({ onInsert, onAIAsk, autoOpen = false }: SpaceInsertPointProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isExpanded, setIsExpanded] = useState(autoOpen);

  const handleCardClick = (cardId: string) => {
    onInsert(cardId);
    setIsExpanded(false);
    setIsHovered(false);
  };

  const handleAISubmit = (prompt: string) => {
    onAIAsk?.(prompt);
    setIsExpanded(false);
    setIsHovered(false);
  };

  const handleClose = () => {
    setIsExpanded(false);
    setIsHovered(false);
  };

  // Handle Escape key to close
  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded]);

  // Collapsed state - just the line
  if (!isExpanded) {
    return (
      <div
        className="relative group py-1"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Insert line with plus button - entire row is clickable */}
        <button
          onClick={() => setIsExpanded(true)}
          className="flex items-center w-full cursor-pointer group/insert active:scale-[0.99] transition-transform"
        >
          {/* Plus icon - positioned in left margin on mobile like drag handle */}
          <div
            className={`flex-shrink-0 w-8 h-8 md:w-5 md:h-5 -ml-4 md:ml-0 rounded-full flex items-center justify-center transition-all duration-200
              text-slate-300 active:text-slate-500 active:scale-95
              md:bg-transparent
              ${isHovered ? 'md:bg-slate-200 md:text-slate-600' : ''}
              md:group-hover/insert:bg-slate-300 md:group-hover/insert:text-slate-700`}
          >
            <Plus className="w-4 h-4 md:w-3 md:h-3" />
          </div>

          {/* Horizontal line */}
          <div
            className={`flex-1 h-px transition-all duration-200 
              bg-slate-200 md:bg-slate-100
              ${isHovered ? 'md:bg-slate-200' : ''}
              md:group-hover/insert:bg-slate-300`}
          />
        </button>
      </div>
    );
  }

  // Expanded state - uses shared ActionCardsPanel
  return (
    <div className="py-6 animate-fade-in-up">
      {/* Close button - top right */}
      <div className="flex justify-end mb-4">
        <button
          onClick={handleClose}
          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          title="Close (Esc)"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <ActionCardsPanel
        onCardClick={handleCardClick}
        onAISubmit={handleAISubmit}
        autoFocusInput={true}
      />
    </div>
  );
};
